import posthog from "posthog-js";
import { api } from "./api";
import { bridge } from "./bridge";
import type {
  OnboardingPrimaryEvent,
  OnboardingSessionRecord,
} from "@shared/onboardingSession";

/**
 * First-party record of the onboarding visit, beaconed to our own API.
 *
 * This exists because the analytics pipeline cannot be trusted with the one
 * question an onboarding experiment must answer — who was shown what, and did
 * they buy. PostHog's exposure loses ~60% of onboarders to races it can't see
 * past: the flags request, the consent opt-in, the identity merge at the
 * account gate. These beacons are consent-independent product bookkeeping
 * (first-party, no third party involved), so they capture reality even when
 * analytics is switched off.
 *
 * Mechanics: one session UUID per visit, held in sessionStorage (it survives
 * the redirect-auth round trip). Every call merges into one pending patch that flushes on a
 * short debounce; the server merges concurrent patches in SQL, so losing a
 * beacon degrades granularity, never correctness. Everything is
 * fire-and-forget — onboarding must never stall on bookkeeping.
 *
 * Stage and milestone helpers no-op unless a session was started, so the
 * booking events that also run outside onboarding don't create phantom rows.
 */

const SESSION_KEY = "onboarding_session_id";

// Private-mode fallback: sessionStorage writes can throw; the id then lives
// only as long as the page, which still covers popup auth (page survives),
// just not redirect auth.
let memorySessionId: string | null = null;

function readSessionId(): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEY) ?? memorySessionId;
  } catch {
    return memorySessionId;
  }
}

function generateSessionId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // Older WebViews. Random enough for a session key.
    return "xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx".replace(/x/g, () =>
      Math.floor(Math.random() * 16).toString(16),
    );
  }
}

type SessionPatch = Omit<OnboardingSessionRecord, "id">;

let pending: SessionPatch | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// Client-side dedup so step re-renders don't re-send what the server would
// ignore anyway (first-write-wins). Per page load; the server dedups across
// page loads.
const sentStages = new Set<string>();
const sentEvents = new Set<OnboardingPrimaryEvent>();

function queue(patch: SessionPatch): void {
  const current: SessionPatch = pending ?? {};
  pending = {
    ...current,
    ...patch,
    experiments: { ...current.experiments, ...patch.experiments },
    decisions: [...(current.decisions ?? []), ...(patch.decisions ?? [])],
    stages: [...(current.stages ?? []), ...(patch.stages ?? [])],
    events: [
      ...new Set([...(current.events ?? []), ...(patch.events ?? [])]),
    ],
  };
  if (!flushTimer) {
    flushTimer = setTimeout(flush, 400);
  }
}

function flush(): void {
  flushTimer = null;
  const id = readSessionId();
  const body = pending;
  pending = null;
  if (!id || !body) return;
  // Auth rides along automatically when signed in (api attaches the Firebase
  // token), which is what links the row to the user after the account gate.
  api.post("/onboarding/sessions/record", { id, ...body }).catch(() => {
    // Fire-and-forget: the server-side purchase stamp and SQL joins make a
    // lost beacon a granularity loss, not a data-integrity one.
  });
}

/**
 * Open (or re-attach to) the visit's session. Called by `OnboardingRoute`
 * when the flow first settles —
 * after redirect auth this re-attaches to the existing row rather than
 * creating a second one.
 */
export function startOnboardingSession(init: {
  experience: string;
  audience?: string;
  experiments?: Record<string, string>;
}): void {
  let id = readSessionId();
  if (!id) {
    id = generateSessionId();
    memorySessionId = id;
    try {
      sessionStorage.setItem(SESSION_KEY, id);
    } catch {
      // Private mode — memorySessionId carries it.
    }
  }
  queue({
    experience: init.experience,
    audience: init.audience,
    distinctId: posthog.get_distinct_id?.() ?? undefined,
    platform: bridge.isNative ? "native" : "web",
    experiments: init.experiments,
  });
}

/** Record a sub-experiment arm this session is part of (flag key → variant). */
export function recordOnboardingExperiment(
  flagKey: string,
  variant: string,
): void {
  if (!readSessionId()) return;
  queue({ experiments: { [flagKey]: variant } });
}

/** Record a stage as cleared, e.g. "stepper:contact-info" or "v3:reveal". */
export function recordOnboardingStage(stage: string): void {
  if (!readSessionId() || sentStages.has(stage)) return;
  sentStages.add(stage);
  queue({ stages: [stage] });
}

/** Record a primary funnel milestone (explicit column on the row). */
export function recordOnboardingPrimaryEvent(
  event: OnboardingPrimaryEvent,
): void {
  if (!readSessionId() || sentEvents.has(event)) return;
  sentEvents.add(event);
  queue({ events: [event] });
}
